import React, { useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Button, TextField, Paper, IconButton,
  InputAdornment, Chip, CircularProgress, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem, Tooltip,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  TableSortLabel, Collapse, Menu, Checkbox, ListItemText, Divider,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon           from "@mui/icons-material/Search";
import RefreshIcon          from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon            from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import { T } from "./adminTheme";

// ─────────────────────────────────────────────────────────────────────────────
// Token helper
// ─────────────────────────────────────────────────────────────────────────────

const tok = (theme) => T(theme.palette.mode);

// ─────────────────────────────────────────────────────────────────────────────
// Layout — Sidebar
// ─────────────────────────────────────────────────────────────────────────────

export const SidebarRoot = styled(Box)(({ theme }) => {
  const t = tok(theme);
  const isDark = theme.palette.mode === "dark";
  return {
    width: 240,
    height: "100vh",
    overflow: "hidden",
    background: isDark
      ? `linear-gradient(180deg, ${t.s0} 0%, ${t.appBg} 100%)`
      : t.s0,
    borderRight: `0.5px solid ${t.border}`,
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 100,
    transition: "background 0.25s ease, border-color 0.25s ease",
    ...(isDark && {
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 1,
        background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.4), transparent)",
        pointerEvents: "none",
      },
    }),
  };
});

export const MainArea = styled(Box)(({ theme }) => ({
  marginLeft: 240,
  minHeight: "100vh",
  background: tok(theme).appBg,
  transition: "background 0.25s ease",
  padding: "16px 12px",
  [theme.breakpoints.up("sm")]:  { padding: "20px 16px" },
  [theme.breakpoints.up("md")]:  { padding: "24px 20px" },
  [theme.breakpoints.up("lg")]:  { padding: "28px 24px" },
  [theme.breakpoints.up("xl")]:  { padding: "28px 32px" },
}));

// ─────────────────────────────────────────────────────────────────────────────
// NavItem
// ─────────────────────────────────────────────────────────────────────────────

export const NavItem = styled(Box, { shouldForwardProp: (p) => p !== "active" })(
  ({ theme, active }) => {
    const t = tok(theme);
    const isDark = theme.palette.mode === "dark";
    return {
      display: "flex",
      alignItems: "center",
      gap: 9,
      padding: "7px 10px",
      cursor: "pointer",
      borderRadius: 8,
      margin: "1px 8px",
      transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
      color: active ? t.heading : t.secondary,
      background: active ? t.navActiveBg : "transparent",
      fontWeight: active ? 600 : 400,
      fontSize: 13,
      letterSpacing: active ? "-0.01em" : "normal",
      borderLeft: active ? `2px solid ${t.blue}` : "2px solid transparent",
      ...(active && isDark && {
        boxShadow: "inset 0 0 20px rgba(59,130,246,0.06)",
      }),
      "&:hover": {
        background: active ? t.navActiveBg : t.s2,
        color: active ? t.heading : t.primary,
        transform: "translateX(1px)",
      },
    };
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Cards & Tables
// ─────────────────────────────────────────────────────────────────────────────

export const SectionCard = styled(Paper)(({ theme }) => {
  const t = tok(theme);
  const isDark = theme.palette.mode === "dark";
  return {
    borderRadius: 14,
    padding: "20px 0 0 0",
    background: t.s0,
    border: `0.5px solid ${t.border}`,
    boxShadow: isDark ? t.cardShadow : "none",
    overflow: "hidden",
    transition: "background 0.25s ease, box-shadow 0.25s ease",
  };
});

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    background: tok(theme).s1,
    color: tok(theme).muted,
    fontWeight: 700,
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    borderBottom: `0.5px solid ${tok(theme).border}`,
    padding: "10px 14px",
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color 0.12s ease",
  "&:hover": { background: tok(theme).s2 },
  "& .MuiTableCell-body": {
    fontSize: 12.5,
    color: tok(theme).primary,
    borderBottom: `0.5px solid ${tok(theme).border}`,
    padding: "11px 14px",
    verticalAlign: "middle",
    lineHeight: 1.5,
  },
  "&:last-child .MuiTableCell-body": { borderBottom: "none" },
}));

// ─────────────────────────────────────────────────────────────────────────────
// FieldLabel
// ─────────────────────────────────────────────────────────────────────────────

export const FieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  color: tok(theme).muted,
  marginBottom: 5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
}));

// ─────────────────────────────────────────────────────────────────────────────
// useSortableTable
// ─────────────────────────────────────────────────────────────────────────────

export function useSortableTable(rows, defaultField = "", defaultDir = "asc") {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortDir("asc");
      }
      return field;
    });
  }, []);

  const sortedRows = useMemo(() => {
    if (!sortField || !rows?.length) return rows || [];

    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const an = Number(av);
      const bn = Number(bv);

      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return sortDir === "asc" ? an - bn : bn - an;
      }

      const ad = Date.parse(av);
      const bd = Date.parse(bv);

      if (!Number.isNaN(ad) && !Number.isNaN(bd)) {
        return sortDir === "asc" ? ad - bd : bd - ad;
      }

      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();

      if (as < bs) return sortDir === "asc" ? -1 : 1;
      if (as > bs) return sortDir === "asc" ? 1 : -1;

      return 0;
    });
  }, [rows, sortField, sortDir]);

  return { sortedRows, sortField, sortDir, handleSort };
}

// ─────────────────────────────────────────────────────────────────────────────
// Column filtering helpers
// ─────────────────────────────────────────────────────────────────────────────

export function getColumnFilterOptions(rows, columnId) {
  const values = new Set();

  (rows || []).forEach((row) => {
    const value = row?.[columnId];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      values.add(String(value));
    }
  });

  return Array.from(values).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );
}

export function applyColumnFilters(rows, columnFilters = {}) {
  const activeFilters = Object.entries(columnFilters).filter(
    ([, selectedValues]) =>
      Array.isArray(selectedValues) && selectedValues.length > 0
  );

  if (activeFilters.length === 0) return rows || [];

  return (rows || []).filter((row) =>
    activeFilters.every(([columnId, selectedValues]) => {
      const rowValue = row?.[columnId];

      if (rowValue === undefined || rowValue === null) return false;

      return selectedValues.includes(String(rowValue));
    })
  );
}

export function clearColumnFilter(columnFilters = {}, columnId) {
  const next = { ...columnFilters };
  delete next[columnId];
  return next;
}

export function hasActiveColumnFilters(columnFilters = {}) {
  return Object.values(columnFilters).some(
    (selectedValues) =>
      Array.isArray(selectedValues) && selectedValues.length > 0
  );
}

export function getActiveColumnFilterCount(columnFilters = {}) {
  return Object.values(columnFilters).reduce(
    (count, selectedValues) =>
      count + (Array.isArray(selectedValues) && selectedValues.length > 0 ? 1 : 0),
    0
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableTableHead
// ─────────────────────────────────────────────────────────────────────────────

export function SortableTableHead({ columns, sortField, sortDir, onSort }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <TableHead>
      <TableRow>
        {columns.map((col) => {
          const sortable = col.sortable !== false;
          const isActive = sortField === col.id;

          return (
            <TableCell
              key={col.id}
              align={col.align || "left"}
              sortDirection={isActive ? sortDir : false}
              sx={{
                background: t.s1,
                color: isActive ? t.blue : t.muted,
                fontWeight: 700,
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                borderBottom: `0.5px solid ${t.border}`,
                padding: "10px 14px",
                userSelect: "none",
                whiteSpace: "nowrap",
                cursor: sortable ? "pointer" : "default",
                transition: "color 0.15s ease",
                ...(col.width && { width: col.width }),
                ...(col.minWidth && { minWidth: col.minWidth }),
                ...(col.maxWidth && { maxWidth: col.maxWidth }),
                "&:hover": sortable ? { color: t.primary } : {},
              }}
            >
              {sortable ? (
                <TableSortLabel
                  active={isActive}
                  direction={isActive ? sortDir : "asc"}
                  onClick={() => onSort(col.id)}
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: `${isActive ? t.blue : t.muted} !important`,
                    "& .MuiTableSortLabel-icon": {
                      fontSize: 12,
                      color: `${isActive ? t.blue : t.muted} !important`,
                      opacity: isActive ? 1 : 0.35,
                      transition: "opacity 0.15s ease",
                    },
                    "&:hover": {
                      color: `${t.primary} !important`,
                      "& .MuiTableSortLabel-icon": { opacity: 1 },
                    },
                  }}
                >
                  {col.label}
                </TableSortLabel>
              ) : (
                col.label
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterableSortableTableHead
// ─────────────────────────────────────────────────────────────────────────────

export function FilterableSortableTableHead({
  columns,
  sourceRows,
  sortField,
  sortDir,
  onSort,
  columnFilters = {},
  onColumnFiltersChange,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  const [anchorEl, setAnchorEl] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);

  const open = Boolean(anchorEl);
  const activeColumnId = activeColumn?.id;
  const selectedValues = activeColumnId
    ? columnFilters?.[activeColumnId] || []
    : [];
  const filterOptions = activeColumnId
    ? getColumnFilterOptions(sourceRows, activeColumnId)
    : [];
  const allSelected =
    filterOptions.length > 0 && selectedValues.length === filterOptions.length;
  const partiallySelected =
    selectedValues.length > 0 && selectedValues.length < filterOptions.length;

  const openFilterMenu = (event, column) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const closeFilterMenu = () => {
    setAnchorEl(null);
    setActiveColumn(null);
  };

  const updateFilters = (nextFilters) => {
    if (typeof onColumnFiltersChange === "function") {
      onColumnFiltersChange(nextFilters);
    }
  };

  const handleToggleValue = (value) => {
    if (!activeColumnId) return;

    const current = columnFilters?.[activeColumnId] || [];
    const exists = current.includes(value);

    const nextSelected = exists
      ? current.filter((item) => item !== value)
      : [...current, value];

    const nextFilters = { ...columnFilters };

    if (nextSelected.length > 0) {
      nextFilters[activeColumnId] = nextSelected;
    } else {
      delete nextFilters[activeColumnId];
    }

    updateFilters(nextFilters);
  };

  const handleSelectAll = () => {
    if (!activeColumnId) return;

    updateFilters({
      ...columnFilters,
      [activeColumnId]: filterOptions,
    });
  };

  const handleClearCurrent = () => {
    if (!activeColumnId) return;

    updateFilters(clearColumnFilter(columnFilters, activeColumnId));
    closeFilterMenu();
  };

  const handleClearAll = () => {
    updateFilters({});
    closeFilterMenu();
  };

  const menuTitle = activeColumn?.label || "Column";

  return (
    <>
      <TableHead>
        <TableRow>
          {columns.map((col) => {
            const sortable = col.sortable !== false;
            const filterable = col.filterable !== false;
            const isActiveSort = sortField === col.id;
            const activeFilterCount = columnFilters?.[col.id]?.length || 0;
            const isActiveFilter = activeFilterCount > 0;

            return (
              <TableCell
                key={col.id}
                align={col.align || "left"}
                sortDirection={isActiveSort ? sortDir : false}
                sx={{
                  background: t.s1,
                  color: isActiveSort ? t.blue : t.muted,
                  fontWeight: 700,
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  borderBottom: `0.5px solid ${t.border}`,
                  padding: "8px 10px",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease",
                  ...(col.width && { width: col.width }),
                  ...(col.minWidth && { minWidth: col.minWidth }),
                  ...(col.maxWidth && { maxWidth: col.maxWidth }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                      col.align === "right" ? "flex-end" : "space-between",
                    gap: 0.75,
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                    {sortable ? (
                      <TableSortLabel
                        active={isActiveSort}
                        direction={isActiveSort ? sortDir : "asc"}
                        onClick={() => onSort(col.id)}
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          maxWidth: "100%",
                          color: `${isActiveSort ? t.blue : t.muted} !important`,
                          "& .MuiTableSortLabel-icon": {
                            fontSize: 12,
                            color: `${isActiveSort ? t.blue : t.muted} !important`,
                            opacity: isActiveSort ? 1 : 0.35,
                            transition: "opacity 0.15s ease",
                          },
                          "&:hover": {
                            color: `${t.primary} !important`,
                            "& .MuiTableSortLabel-icon": { opacity: 1 },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {col.label}
                        </Box>
                      </TableSortLabel>
                    ) : (
                      <Typography
                        component="span"
                        sx={{
                          display: "block",
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {col.label}
                      </Typography>
                    )}
                  </Box>

                  {filterable && (
                    <Tooltip
                      title={
                        isActiveFilter
                          ? `${activeFilterCount} selected in ${col.label}`
                          : `Filter ${col.label}`
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(event) => openFilterMenu(event, col)}
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "7px",
                          color: isActiveFilter ? t.blue : t.muted,
                          background: isActiveFilter ? t.blueAlpha : "transparent",
                          border: `0.5px solid ${
                            isActiveFilter ? `${t.blue}40` : "transparent"
                          }`,
                          flexShrink: 0,
                          transition: "all 0.15s ease",
                          "&:hover": {
                            background: t.s2,
                            color: t.primary,
                            borderColor: t.borderHov,
                          },
                        }}
                      >
                        <FilterListIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeFilterMenu}
        PaperProps={{
          sx: {
            width: 280,
            maxHeight: 380,
            borderRadius: "12px",
            border: `0.5px solid ${t.border}`,
            background: t.s0,
            boxShadow: t.cardShadow,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.15 }}>
          <Typography
            sx={{ fontSize: 12, fontWeight: 800, color: t.heading, mb: 0.3 }}
          >
            Filter: {menuTitle}
          </Typography>

          <Typography sx={{ fontSize: 11, color: t.muted, lineHeight: 1.35 }}>
            Select values from the currently loaded result set.
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ display: "flex", gap: 0.75, px: 1, py: 0.85, flexWrap: "wrap" }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={filterOptions.length === 0 || allSelected}
          >
            Select All
          </Button>

          <Button
            size="small"
            color="warning"
            onClick={handleClearCurrent}
            disabled={!activeColumnId || selectedValues.length === 0}
          >
            Clear
          </Button>

          <Button
            size="small"
            color="error"
            onClick={handleClearAll}
            disabled={!hasActiveColumnFilters(columnFilters)}
          >
            Clear All
          </Button>
        </Box>

        <Divider />

        <Box sx={{ maxHeight: 260, overflowY: "auto", py: 0.5 }}>
          {filterOptions.length === 0 ? (
            <MenuItem disabled dense>
              <ListItemText
                primary="No values available"
                primaryTypographyProps={{ fontSize: 12, color: t.muted }}
              />
            </MenuItem>
          ) : (
            filterOptions.map((option) => (
              <MenuItem
                key={option}
                dense
                onClick={() => handleToggleValue(option)}
                sx={{
                  minHeight: 34,
                  color: t.primary,
                  "&:hover": { background: t.s2 },
                }}
              >
                <Checkbox
                  size="small"
                  checked={selectedValues.includes(option)}
                  indeterminate={false}
                  sx={{ p: 0.5, mr: 0.75 }}
                />

                <ListItemText
                  primary={option}
                  primaryTypographyProps={{
                    fontSize: 12,
                    noWrap: true,
                    title: option,
                  }}
                />
              </MenuItem>
            ))
          )}
        </Box>

        {partiallySelected && (
          <>
            <Divider />
            <Box sx={{ px: 1.5, py: 0.8 }}>
              <Typography sx={{ fontSize: 11, color: t.muted }}>
                {selectedValues.length} of {filterOptions.length} value
                {filterOptions.length !== 1 ? "s" : ""} selected
              </Typography>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SearchBar
// ─────────────────────────────────────────────────────────────────────────────

export function SearchBar({ value, onChange, placeholder }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search..."}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 15, color: t.muted }} />
          </InputAdornment>
        ),
      }}
      sx={{
        width: { xs: "100%", sm: 200, md: 230 },
        "& .MuiOutlinedInput-root": {
          background: t.s2,
          borderRadius: "8px",
          fontSize: 13,
          height: 34,
        },
      }}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TabHeader
// ─────────────────────────────────────────────────────────────────────────────

export function TabHeader({
  count,
  noun,
  search,
  onSearch,
  onRefresh,
  onAdd,
  btnLabel,
  hideSearch = false,
  hideAdd = false,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        gap: { xs: 1.5, sm: 0 },
        px: 2.5,
        pb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: t.heading,
              letterSpacing: "-0.01em",
              lineHeight: 1.2,
            }}
          >
            {noun}
          </Typography>

          <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.2, fontWeight: 500 }}>
            {count} record{count !== 1 ? "s" : ""}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 1.25,
            py: 0.25,
            borderRadius: "20px",
            background: isDark ? t.s3 : t.s2,
            border: `0.5px solid ${t.border}`,
            minWidth: 32,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.secondary }}>
            {count}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          width: { xs: "100%", sm: "auto" },
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        {!hideSearch && (
          <SearchBar
            value={search}
            onChange={onSearch}
            placeholder={`Search ${noun.toLowerCase()}...`}
          />
        )}

        <IconButton
          onClick={onRefresh}
          size="small"
          sx={{
            border: `0.5px solid ${t.border}`,
            borderRadius: "8px",
            width: 34,
            height: 34,
            color: t.muted,
            transition: "all 0.15s ease",
            "&:hover": {
              background: t.s2,
              color: t.secondary,
              borderColor: t.borderHov,
              transform: "rotate(15deg)",
            },
          }}
        >
          <RefreshIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {!hideAdd && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 14 }} />}
            onClick={onAdd}
            sx={{
              borderRadius: "8px",
              fontSize: 12,
              fontWeight: 600,
              px: 1.5,
              height: 34,
              letterSpacing: "0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {btnLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CreateDialog
// ─────────────────────────────────────────────────────────────────────────────

export function CreateDialog({ open, onClose, title, icon, color, children }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
            borderBottom: `0.5px solid ${t.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: `${color}18`,
                border: `0.5px solid ${color}30`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color,
                boxShadow: `0 0 10px ${color}15`,
              }}
            >
              {icon}
            </Box>

            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                color: t.heading,
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: t.muted,
              width: 30,
              height: 30,
              borderRadius: "7px",
              "&:hover": { background: t.s2, color: t.secondary },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: "22px !important" }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BoolChip / AvailabilityChip
// ─────────────────────────────────────────────────────────────────────────────

export function BoolChip({ value, trueLabel = "Active", falseLabel = "Inactive" }) {
  const active = Number(value) === 1;

  return (
    <Chip
      size="small"
      label={active ? trueLabel : falseLabel}
      color={active ? "success" : "default"}
    />
  );
}

export function AvailabilityChip({ value }) {
  const available = Number(value) === 1;

  return (
    <Chip
      size="small"
      label={available ? "Available" : "In Use"}
      color={available ? "success" : "warning"}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FilterField
// ─────────────────────────────────────────────────────────────────────────────

export function FilterField({ label, value, onChange, type = "text" }) {
  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={onChange}
      type={type}
      InputLabelProps={type === "datetime-local" ? { shrink: true } : undefined}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// InlinePromptDialog
// ─────────────────────────────────────────────────────────────────────────────

export function InlinePromptDialog({
  open,
  onClose,
  onConfirm,
  title,
  label,
  value,
  onChange,
  confirmLabel = "Confirm",
  confirmColor = "primary",
  selectOptions,
  children,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDanger = confirmColor === "error";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={{ pt: "14px !important" }}>
        {selectOptions ? (
          <FormControl fullWidth size="small">
            <FieldLabel sx={{ mb: 0.75 }}>{label}</FieldLabel>

            <Select value={value} onChange={(e) => onChange(e.target.value)}>
              {selectOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth
            size="small"
            label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && value) onConfirm();
            }}
          />
        )}

        {children}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">
          Cancel
        </Button>

        <Button
          variant={isDanger ? "text" : "contained"}
          color={confirmColor}
          size="small"
          onClick={onConfirm}
          disabled={!value}
          sx={
            isDanger
              ? {
                  background: t.redAlpha,
                  color: theme.palette.mode === "dark" ? "#f87171" : "#dc2626",
                  border: `0.5px solid ${t.red}40`,
                  "&:hover": { background: `${t.red}18` },
                  "&:disabled": { opacity: 0.4 },
                }
              : {}
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SkeletonRows
// ─────────────────────────────────────────────────────────────────────────────

export function SkeletonRows({ colSpan, rows = 6 }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: colSpan }).map((__, j) => (
            <TableCell
              key={j}
              sx={{
                borderBottom: `0.5px solid ${t.border}`,
                py: "12px",
                px: "14px",
              }}
            >
              <Skeleton
                variant="text"
                width={
                  j === 0
                    ? "55%"
                    : j === colSpan - 1
                    ? "75%"
                    : `${38 + ((i + j) % 4) * 12}%`
                }
                height={14}
                sx={{
                  bgcolor: t.s3,
                  borderRadius: "5px",
                  "&::after": {
                    background: `linear-gradient(90deg, transparent, ${t.s2}, transparent)`,
                  },
                }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TableStateWrapper
// ─────────────────────────────────────────────────────────────────────────────

export function TableStateWrapper({
  loading,
  empty,
  emptyMessage = "No records found.",
  colSpan,
  minHeight = 320,
  skeletonRows = 6,
  children,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  if (loading) {
    return (
      <Box sx={{ minHeight, position: "relative" }}>
        <TableContainer sx={{ minHeight }}>
          <Box
            component="table"
            sx={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
          >
            <TableBody>
              <SkeletonRows colSpan={colSpan} rows={skeletonRows} />
            </TableBody>
          </Box>
        </TableContainer>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              py: 0.75,
              borderRadius: "20px",
              background: t.s2,
              border: `0.5px solid ${t.borderHov}`,
              boxShadow: t.cardShadow,
              opacity: 0.95,
            }}
          >
            <CircularProgress size={12} sx={{ color: t.blue }} />

            <Typography sx={{ fontSize: 11, color: t.secondary, fontWeight: 600 }}>
              Loading…
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (empty) {
    return (
      <Box
        sx={{
          minHeight,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
          py: 4,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            background: t.s2,
            border: `0.5px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 0.5,
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "12px",
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.05)`,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 2,
              background: t.muted,
              borderRadius: 1,
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 13, color: t.secondary, fontWeight: 500 }}>
          {emptyMessage}
        </Typography>

        <Typography sx={{ fontSize: 11, color: t.muted }}>
          Try adjusting your search or filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      {children}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TouchTooltip
// ─────────────────────────────────────────────────────────────────────────────

export function TouchTooltip({ title, children }) {
  return (
    <Tooltip
      title={title}
      arrow
      placement="top"
      enterTouchDelay={0}
      leaveTouchDelay={3000}
      disableInteractive={false}
    >
      <span>{children}</span>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RecentShifts helpers
// ─────────────────────────────────────────────────────────────────────────────

export const CACHE_DURATION_MS = 5 * 60 * 1000;

export const formatName = (name, maxLength = 10) => {
  if (!name) return "";
  const trimmed = name.trim();
  return trimmed.length > maxLength ? trimmed.slice(0, maxLength) + "…" : trimmed;
};

export const splitNames = (value) =>
  value ? value.split(",").map((n) => n.trim()).filter(Boolean) : [];

export const getRowKey = (row) =>
  `${row.Employee_ID}-${row.shift_loggedIn_DateTime}`;

export function parseHHMMSSToMinutes(hhmmss) {
  if (!hhmmss || typeof hhmmss !== "string") return 0;

  const parts = hhmmss.split(":");

  if (parts.length !== 3) return 0;

  const [h, m, s] = parts.map((n) => Number(n || 0));

  if ([h, m, s].some((x) => Number.isNaN(x))) return 0;

  return h * 60 + m + Math.floor(s / 60);
}

// ─────────────────────────────────────────────────────────────────────────────
// ExpandToggleButton
// ─────────────────────────────────────────────────────────────────────────────

export function ExpandToggleButton({ open, onClick }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 26,
        height: 26,
        borderRadius: "6px",
        color: open ? t.blue : t.muted,
        backgroundColor: open ? t.blueAlpha : "transparent",
        border: `0.5px solid ${open ? t.blue + "40" : "transparent"}`,
        transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
        "& svg": {
          fontSize: 18,
          transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
        },
        "&:hover": {
          backgroundColor: t.s2,
          color: t.primary,
        },
      }}
    >
      <KeyboardArrowDownIcon />
    </IconButton>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DetailField
// ─────────────────────────────────────────────────────────────────────────────

export function DetailField({ label, value }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          color: t.muted,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          mb: 0.4,
        }}
      >
        {label}
      </Typography>

      <Box sx={{ fontSize: 12.5, color: t.primary, minHeight: 20, fontWeight: 500 }}>
        {value ?? <span style={{ color: t.muted }}>—</span>}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DetailPanel
// ─────────────────────────────────────────────────────────────────────────────

export function DetailPanel({ title, accent = "#3b82f6", children }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        mx: 2,
        my: 1.5,
        p: 2,
        borderRadius: "10px",
        backgroundColor: t.s1,
        border: `0.5px solid ${t.border}`,
        borderLeft: `2.5px solid ${accent}`,
        ...(isDark && {
          boxShadow: `inset 0 0 20px rgba(0,0,0,0.15)`,
        }),
      }}
    >
      {title && (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: accent,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ExpandableRow
// ─────────────────────────────────────────────────────────────────────────────

export function ExpandableRow({ open, colSpan, children }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const [mainRow, detail] = React.Children.toArray(children);

  return (
    <>
      {mainRow}

      <TableRow>
        <TableCell
          colSpan={colSpan}
          sx={{
            p: 0,
            borderBottom: open ? `0.5px solid ${t.border}` : "none",
            backgroundColor: t.s1,
          }}
        >
          <Collapse in={open} timeout={200} unmountOnExit>
            {detail}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RowActionButton
// ─────────────────────────────────────────────────────────────────────────────

export function RowActionButton({
  title,
  icon,
  onClick,
  color = "default",
  active = false,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  const colorMap = {
    default: { base: t.secondary, bg: t.s2, activeBg: t.s3 },
    primary: { base: t.blue, bg: t.blueAlpha, activeBg: t.blueAlpha },
    warning: { base: t.amber, bg: t.amberAlpha, activeBg: t.amberAlpha },
    error: { base: t.red, bg: t.redAlpha, activeBg: t.redAlpha },
    success: { base: t.green, bg: t.greenAlpha, activeBg: t.greenAlpha },
  };

  const c = colorMap[color] || colorMap.default;

  return (
    <Tooltip title={title} arrow placement="top" enterDelay={250}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          color: c.base,
          backgroundColor: active ? c.activeBg : "transparent",
          border: `0.5px solid ${active ? c.base + "45" : "transparent"}`,
          transition: "all 0.14s cubic-bezier(0.4,0,0.2,1)",
          "& svg": { fontSize: 15 },
          "&:hover": {
            backgroundColor: c.bg,
            border: `0.5px solid ${c.base}35`,
            transform: "scale(1.05)",
          },
          "&:active": { transform: "scale(0.97)" },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ReleaseSessionDialog
// ─────────────────────────────────────────────────────────────────────────────

export function ReleaseSessionDialog({
  open,
  onClose,
  employeeId,
  machineCode,
  entityType = "user",
  loading,
  analyzeData,
  onConfirmRelease,
  onGoToRecovery,
  onMarkOutFromRoster,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [reason, setReason] = useState("");

  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const titleMap = {
    user: "Release User Session",
    employee: "Release Employee",
    workstation: "Release Workstation",
  };

  const cleanMessageMap = {
    user: "This user has no open shifts, operations, or job summaries. The session can be released safely.",
    employee: "This employee has no active production work. They can be marked as available safely.",
    workstation: "This workstation has no active production session. It can be released safely.",
  };

  const cleanButtonMap = {
    user: "Release Session",
    employee: "Confirm Release",
    workstation: "Release Workstation",
  };

  const hasActiveOwnSession =
    !!analyzeData &&
    (analyzeData.hasOpenShift ||
      analyzeData.hasOpenJobsummary ||
      analyzeData.ongoingJobOpsCount > 0 ||
      analyzeData.ongoingNonJobOpsCount > 0);

  const isRosterOnly =
    !!analyzeData && !hasActiveOwnSession && !!analyzeData.rosterMembership;

  const roleLabelMap = {
    operator: "Operator",
    asstOperator: "Asst Operator",
    helper: "Helper",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle>
        {titleMap[entityType]}

        <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.3, fontWeight: 400 }}>
          {employeeId && (
            <>
              Employee ID:{" "}
              <span
                style={{
                  fontFamily: "monospace",
                  color: t.primary,
                  fontWeight: 700,
                }}
              >
                {employeeId}
              </span>
            </>
          )}

          {employeeId && machineCode && " • "}

          {machineCode && (
            <>
              Workstation:{" "}
              <span
                style={{
                  fontFamily: "monospace",
                  color: t.primary,
                  fontWeight: 700,
                }}
              >
                {machineCode}
              </span>
            </>
          )}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: "14px !important" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 4,
              gap: 1.5,
            }}
          >
            <CircularProgress size={28} sx={{ color: t.blue }} />

            <Typography sx={{ fontSize: 12, color: t.muted }}>
              Checking for active sessions…
            </Typography>
          </Box>
        ) : hasActiveOwnSession ? (
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              backgroundColor: t.redAlpha,
              border: `0.5px solid ${t.red}35`,
              borderLeft: `2.5px solid ${t.red}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: isDark ? "#f87171" : "#dc2626",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1,
              }}
            >
              ⚠ Active session detected
            </Typography>

            <Typography sx={{ fontSize: 13, color: t.primary, mb: 1.5, lineHeight: 1.6 }}>
              This release would leave production data in an inconsistent state.
              Please close the session properly using Session Recovery first.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, mb: 1.5 }}>
              {analyzeData.hasOpenShift && (
                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • Open shift on machine{" "}
                  <b>{analyzeData.machineCode || analyzeData.openShift?.machineCode || "—"}</b>
                </Typography>
              )}

              {analyzeData.hasOpenJobsummary && (
                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • Unclosed job summary
                  {analyzeData.openJobsummary?.jobNo
                    ? ` (Job ${analyzeData.openJobsummary.jobNo})`
                    : ""}
                </Typography>
              )}

              {analyzeData.ongoingJobOpsCount > 0 && (
                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • {analyzeData.ongoingJobOpsCount} ongoing job operation
                  {analyzeData.ongoingJobOpsCount > 1 ? "s" : ""}
                </Typography>
              )}

              {analyzeData.ongoingNonJobOpsCount > 0 && (
                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • {analyzeData.ongoingNonJobOpsCount} ongoing non-job operation
                  {analyzeData.ongoingNonJobOpsCount > 1 ? "s" : ""}
                </Typography>
              )}

              {analyzeData.caseCode && analyzeData.caseCode !== "CASE_00" && (
                <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.5 }}>
                  Detected case:{" "}
                  <span style={{ fontFamily: "monospace" }}>
                    {analyzeData.caseCode}
                  </span>
                </Typography>
              )}
            </Box>

            <Typography sx={{ fontSize: 12, color: t.secondary, lineHeight: 1.5 }}>
              Session Recovery will close the shift, finalize all operations, and
              record your reason in the audit log.
            </Typography>
          </Box>
        ) : isRosterOnly ? (
          <>
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                backgroundColor: t.amberAlpha,
                border: `0.5px solid ${t.amber}35`,
                borderLeft: `2.5px solid ${t.amber}`,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: isDark ? "#fbbf24" : "#d97706",
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 1,
                }}
              >
                ⚠ Member of another shift&apos;s roster
              </Typography>

              <Typography sx={{ fontSize: 13, color: t.primary, mb: 1.5, lineHeight: 1.6 }}>
                This employee is currently part of a shift hosted by another operator.
                They do not have their own login session.
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mb: 1 }}>
                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • Host operator:{" "}
                  <b>{analyzeData.rosterMembership.hostEmployeeId}</b>
                </Typography>

                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • On machine:{" "}
                  <b>{analyzeData.rosterMembership.hostMachineCode}</b>
                </Typography>

                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • Role:{" "}
                  <b>
                    {roleLabelMap[analyzeData.rosterMembership.role] ||
                      analyzeData.rosterMembership.role}
                  </b>
                </Typography>

                <Typography sx={{ fontSize: 12, color: t.primary }}>
                  • Shift started: {analyzeData.rosterMembership.hostShiftStart}
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 12, color: t.secondary, mt: 1, lineHeight: 1.5 }}>
                Choose <b>Mark Out from Roster</b> to log the out-time on the host shift
                and free this employee. The host shift continues running.
                Choose <b>Full Session Recovery</b> if you also need to close the host shift.
              </Typography>
            </Box>

            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: t.muted,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                mb: 0.6,
              }}
            >
              Reason (required) *
            </Typography>

            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this employee being marked out?"
              autoFocus
            />
          </>
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              backgroundColor: t.greenAlpha,
              border: `0.5px solid ${t.green}35`,
              borderLeft: `2.5px solid ${t.green}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: isDark ? "#34d399" : "#059669",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 1,
              }}
            >
              ✓ No active session
            </Typography>

            <Typography sx={{ fontSize: 13, color: t.primary, lineHeight: 1.6 }}>
              {cleanMessageMap[entityType]}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">
          Cancel
        </Button>

        {!loading && hasActiveOwnSession && (
          <Button variant="contained" color="warning" size="small" onClick={onGoToRecovery}>
            Go to Session Recovery
          </Button>
        )}

        {!loading && isRosterOnly && (
          <>
            <Button variant="outlined" color="warning" size="small" onClick={onGoToRecovery}>
              Full Session Recovery
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!reason.trim()}
              onClick={() => onMarkOutFromRoster && onMarkOutFromRoster(reason.trim())}
            >
              Mark Out from Roster
            </Button>
          </>
        )}

        {!loading && !hasActiveOwnSession && !isRosterOnly && (
          <Button variant="contained" color="primary" size="small" onClick={onConfirmRelease}>
            {cleanButtonMap[entityType]}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StatCard
// ─────────────────────────────────────────────────────────────────────────────

export function StatCard({ label, value, color, icon }) {
  const theme = useTheme();
  const t = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: "12px",
        p: 2,
        background: t.s0,
        border: `0.5px solid ${t.border}`,
        borderTop: `2px solid ${color}`,
        display: "flex",
        alignItems: "center",
        gap: 1.75,
        transition: "all 0.18s ease",
        boxShadow: isDark ? t.cardShadow : "none",
        "&:hover": {
          borderColor: color,
          transform: "translateY(-1px)",
          boxShadow: isDark
            ? `0 4px 20px rgba(0,0,0,0.4), 0 0 0 0.5px ${color}30`
            : `0 4px 12px rgba(0,0,0,0.08), 0 0 0 0.5px ${color}25`,
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          background: `${color}15`,
          border: `0.5px solid ${color}25`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          flexShrink: 0,
          boxShadow: `0 0 12px ${color}12`,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 700,
            color: t.heading,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
          }}
        >
          {value ?? "—"}
        </Typography>

        <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.3, fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const NEW_GROUP_SENTINEL = "__new__";

export const USER_ROLE_OPTIONS = [
  "operator",
  "supervisor",
  "machinesupervisor",
  "planner",
  "hr",
  "admin",
];